import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Plus, Printer, Trash2, Pencil, ClipboardList, Trophy, Plane, Home, Calendar as CalIcon,
  RefreshCw, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { printMatchSheet } from "../lib/documents";

const STATUS_BADGE = {
  brouillon: { cls: "bg-[#FBC02D]/15 text-[#F57F17] border-[#FBC02D]/40", label: "Brouillon" },
  validee: { cls: "bg-[#1976D2]/15 text-[#1976D2] border-[#1976D2]/40", label: "Validée" },
  jouee: { cls: "bg-[#2E7D32]/15 text-[#2E7D32] border-[#2E7D32]/40", label: "Jouée" },
};

const EVENT_TYPES = [
  { v: "but", label: "But" },
  { v: "passe_decisive", label: "Passe décisive" },
  { v: "carton_jaune", label: "Carton jaune" },
  { v: "carton_rouge", label: "Carton rouge" },
  { v: "remplacement", label: "Remplacement" },
  { v: "blessure", label: "Blessure" },
];

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  time: "",
  opponent: "",
  location: "",
  competition: "",
  home_away: "domicile",
  referee: "",
  coach_id: "",
  assistant_id: "",
  doctor_id: "",
  captain_id: "",
  formation: "",
  lineup: [],
  score_home: "",
  score_away: "",
  events: [],
  notes: "",
  status: "brouillon",
});

export default function MatchSheets() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [players, setPlayers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/match-sheets").then((r) => r.data),
      api.get("/players").then((r) => r.data),
      api.get("/employees").then((r) => r.data).catch(() => []),
    ]);
    setList(a);
    setPlayers(b);
    setEmployees(c);
  };
  useEffect(() => { load(); }, []);

  const playerName = (id) => {
    const p = players.find((x) => x.id === id);
    return p ? `${p.last_name.toUpperCase()} ${p.first_name}` : "—";
  };
  const empName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : "—";
  };

  const filtered = useMemo(() => tab === "all" ? list : list.filter((m) => m.status === tab), [list, tab]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = async (m) => {
    setEditing(m);
    setForm({
      ...emptyForm(),
      ...m,
      date: m.date ? m.date.slice(0, 10) : "",
      score_home: m.score_home ?? "",
      score_away: m.score_away ?? "",
      lineup: m.lineup || [],
      events: m.events || [],
      coach_id: m.coach_id || "",
      assistant_id: m.assistant_id || "",
      doctor_id: m.doctor_id || "",
      captain_id: m.captain_id || "",
    });
    setOpen(true);
  };

  const togglePlayerInLineup = (playerId, role) => {
    setForm((f) => {
      const existing = f.lineup.find((l) => l.player_id === playerId);
      const player = players.find((p) => p.id === playerId);
      if (existing) {
        if (existing.role === role) {
          // remove
          return { ...f, lineup: f.lineup.filter((l) => l.player_id !== playerId) };
        }
        // change role
        return { ...f, lineup: f.lineup.map((l) => l.player_id === playerId ? { ...l, role } : l) };
      }
      const tits = f.lineup.filter((l) => l.role === "titulaire").length;
      const rems = f.lineup.filter((l) => l.role === "remplacant").length;
      if (role === "titulaire" && tits >= 11) {
        toast.error("Maximum 11 titulaires");
        return f;
      }
      if (role === "remplacant" && rems >= 12) {
        toast.error("Maximum 12 remplaçants");
        return f;
      }
      return {
        ...f,
        lineup: [...f.lineup, {
          player_id: playerId,
          jersey_number: player?.jersey_number ?? null,
          position: player?.position ?? "",
          role,
        }],
      };
    });
  };

  const addEvent = () => {
    setForm((f) => ({
      ...f,
      events: [...f.events, { minute: "", type: "but", player_id: "", player_in_id: "", note: "" }],
    }));
  };
  const updateEvent = (idx, patch) => {
    setForm((f) => ({ ...f, events: f.events.map((e, i) => i === idx ? { ...e, ...patch } : e) }));
  };
  const removeEvent = (idx) => {
    setForm((f) => ({ ...f, events: f.events.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.opponent || !form.date) {
      toast.error("Adversaire et date requis");
      return;
    }
    const payload = {
      ...form,
      score_home: form.score_home === "" ? null : parseInt(form.score_home, 10),
      score_away: form.score_away === "" ? null : parseInt(form.score_away, 10),
      coach_id: form.coach_id || null,
      assistant_id: form.assistant_id || null,
      doctor_id: form.doctor_id || null,
      captain_id: form.captain_id || null,
      time: form.time || null,
      location: form.location || null,
      competition: form.competition || null,
      referee: form.referee || null,
      formation: form.formation || null,
      notes: form.notes || null,
      lineup: form.lineup.map((l) => ({
        ...l,
        jersey_number: l.jersey_number === "" || l.jersey_number === null ? null : parseInt(l.jersey_number, 10),
      })),
      events: form.events
        .filter((ev) => ev.player_id)
        .map((ev) => ({
          ...ev,
          minute: ev.minute === "" ? null : parseInt(ev.minute, 10),
          player_in_id: ev.player_in_id || null,
          note: ev.note || null,
        })),
    };
    try {
      if (editing) {
        await api.patch(`/match-sheets/${editing.id}`, payload);
        toast.success("Feuille de match mise à jour");
      } else {
        await api.post("/match-sheets", payload);
        toast.success("Feuille de match créée");
      }
      setOpen(false); setEditing(null); setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Supprimer la feuille de match contre ${m.opponent} ?`)) return;
    try {
      await api.delete(`/match-sheets/${m.id}`);
      toast.success("Supprimée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onSync = async (m) => {
    if (!window.confirm(
      `Synchroniser les performances de cette feuille ?\n\n` +
      `Cela créera (ou remplacera) les statistiques individuelles dans la page Performances ` +
      `pour chaque joueur ayant joué ou marqué.`
    )) return;
    try {
      const { data } = await api.post(`/match-sheets/${m.id}/sync-performances`);
      toast.success(`${data.performances_created} performance${data.performances_created > 1 ? "s" : ""} créée${data.performances_created > 1 ? "s" : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onPrint = (m) => printMatchSheet(user?.club, m, players, employees);

  const lineupTitulaires = form.lineup.filter((l) => l.role === "titulaire");
  const lineupRemplacants = form.lineup.filter((l) => l.role === "remplacant");
  const playerInLineup = (id) => form.lineup.find((l) => l.player_id === id);

  return (
    <div>
      <PageHeader
        overline="Compétition"
        title="Feuilles de match"
        description="Composition, encadrement, événements et export PDF."
        actions={
          <Button onClick={openCreate} data-testid="create-match-sheet-btn" className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle feuille
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="ms-tab-all">Toutes ({list.length})</TabsTrigger>
          <TabsTrigger value="brouillon" data-testid="ms-tab-brouillon">Brouillons</TabsTrigger>
          <TabsTrigger value="validee" data-testid="ms-tab-validee">Validées</TabsTrigger>
          <TabsTrigger value="jouee" data-testid="ms-tab-jouee">Jouées</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E0E0E0] rounded-md p-12 text-center">
          <ClipboardList className="w-10 h-10 text-[#C62828]/40 mx-auto mb-4" />
          <h3 className="font-display text-xl font-bold">Aucune feuille de match</h3>
          <p className="text-sm text-[#616161] mt-2">Créez votre première feuille pour préparer un match.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="match-sheets-grid">
          {filtered.map((m) => {
            const sb = STATUS_BADGE[m.status] || STATUS_BADGE.brouillon;
            const titCount = (m.lineup || []).filter((l) => l.role === "titulaire").length;
            const remCount = (m.lineup || []).filter((l) => l.role === "remplacant").length;
            return (
              <div key={m.id} className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden hover:border-[var(--club-primary,#C62828)]/40 transition" data-testid={`match-sheet-card-${m.id}`}>
                <div className="p-4 border-b border-[#E0E0E0]">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className={`${sb.cls} border`}>
                      {sb.label}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-[#616161]">
                      {m.home_away === "domicile" ? <Home className="w-3.5 h-3.5" /> : <Plane className="w-3.5 h-3.5" />}
                      {m.home_away === "domicile" ? "Domicile" : "Extérieur"}
                    </div>
                  </div>
                  <p className="font-display text-lg font-bold truncate" title={m.opponent}>
                    vs {m.opponent}
                  </p>
                  <p className="text-xs text-[#616161] mt-1 flex items-center gap-1">
                    <CalIcon className="w-3 h-3" />
                    {new Date(m.date).toLocaleDateString("fr-FR", { dateStyle: "long" })}
                    {m.time ? ` · ${m.time}` : ""}
                  </p>
                  {m.competition && (
                    <p className="text-[11px] text-[#616161] mt-1 flex items-center gap-1">
                      <Trophy className="w-3 h-3" /> {m.competition}
                    </p>
                  )}
                </div>
                {m.score_home !== null && m.score_home !== undefined && (
                  <div className="px-4 py-3 bg-[#0A4628] text-white flex items-center justify-center gap-3">
                    <span className="text-sm opacity-80 truncate max-w-[80px] text-right">{m.home_away === "domicile" ? user?.club?.name : m.opponent}</span>
                    <span className="font-display text-2xl font-extrabold">{m.score_home} - {m.score_away}</span>
                    <span className="text-sm opacity-80 truncate max-w-[80px]">{m.home_away === "domicile" ? m.opponent : user?.club?.name}</span>
                  </div>
                )}
                <div className="px-4 py-3 text-xs text-[#616161] flex justify-between border-t border-[#E0E0E0]">
                  <span>{titCount} titulaire{titCount > 1 ? "s" : ""}</span>
                  <span>{remCount} remplaçant{remCount > 1 ? "s" : ""}</span>
                  <span>{(m.events || []).length} événement{(m.events || []).length > 1 ? "s" : ""}</span>
                </div>
                <div className="px-4 py-2 border-t border-[#E0E0E0] flex justify-end gap-1">
                  {m.status === "jouee" && (
                    <Button size="sm" variant="ghost" onClick={() => onSync(m)} title="Synchroniser les performances" data-testid={`sync-match-${m.id}`}>
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-match-${m.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onPrint(m)} data-testid={`print-match-${m.id}`}>
                    <Printer className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(m)}>
                    <Trash2 className="w-3.5 h-3.5 text-[#C62828]" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm()); } }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="match-sheet-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier la feuille de match" : "Nouvelle feuille de match"}</DialogTitle>
            <DialogDescription>
              Renseignez les informations du match. Vous pourrez modifier les événements et le score après le match.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            {/* En-tête match */}
            <section>
              <h3 className="text-xs tracking-overline text-[#616161] mb-2">Informations générales</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>Date *</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ms-date" /></div>
                <div><Label>Heure</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Adversaire *</Label>
                  <Input required value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} data-testid="ms-opponent" />
                </div>
                <div>
                  <Label>Lieu / Stade</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <Label>Compétition</Label>
                  <Input value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} placeholder="Championnat, Coupe…" />
                </div>
                <div>
                  <Label>Domicile / Extérieur</Label>
                  <Select value={form.home_away} onValueChange={(v) => setForm({ ...form, home_away: v })}>
                    <SelectTrigger data-testid="ms-home-away"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domicile">Domicile</SelectItem>
                      <SelectItem value="exterieur">Extérieur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="ms-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brouillon">Brouillon</SelectItem>
                      <SelectItem value="validee">Validée</SelectItem>
                      <SelectItem value="jouee">Jouée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Arbitre</Label><Input value={form.referee} onChange={(e) => setForm({ ...form, referee: e.target.value })} /></div>
                <div><Label>Formation</Label><Input value={form.formation} onChange={(e) => setForm({ ...form, formation: e.target.value })} placeholder="4-3-3" /></div>
              </div>
            </section>

            {/* Encadrement */}
            <section>
              <h3 className="text-xs tracking-overline text-[#616161] mb-2">Encadrement</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>Entraîneur</Label>
                  <Select value={form.coach_id || "none"} onValueChange={(v) => setForm({ ...form, coach_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Adjoint</Label>
                  <Select value={form.assistant_id || "none"} onValueChange={(v) => setForm({ ...form, assistant_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Médecin</Label>
                  <Select value={form.doctor_id || "none"} onValueChange={(v) => setForm({ ...form, doctor_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Capitaine</Label>
                  <Select value={form.captain_id || "none"} onValueChange={(v) => setForm({ ...form, captain_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Composition */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs tracking-overline text-[#616161]">
                  Composition · {lineupTitulaires.length}/11 titulaires · {lineupRemplacants.length} remplaçants
                </h3>
              </div>
              <div className="border border-[#E0E0E0] rounded-md max-h-72 overflow-y-auto divide-y divide-[#E0E0E0]" data-testid="ms-lineup-list">
                {players.length === 0 ? (
                  <p className="p-4 text-sm text-[#616161] text-center">Aucun joueur dans l&apos;effectif.</p>
                ) : players.map((p) => {
                  const lp = playerInLineup(p.id);
                  return (
                    <div key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#F4F5F7]">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {p.jersey_number != null && <span className="font-mono text-xs text-[var(--club-primary,#C62828)] mr-2">#{p.jersey_number}</span>}
                          {p.last_name.toUpperCase()} {p.first_name}
                        </p>
                        <p className="text-[11px] text-[#616161]">{p.position || "—"}</p>
                      </div>
                      <Button
                        type="button" size="sm"
                        variant={lp?.role === "titulaire" ? "default" : "outline"}
                        onClick={() => togglePlayerInLineup(p.id, "titulaire")}
                        className={lp?.role === "titulaire" ? "btn-club" : ""}
                        data-testid={`ms-titulaire-${p.id}`}
                      >
                        Titulaire
                      </Button>
                      <Button
                        type="button" size="sm"
                        variant={lp?.role === "remplacant" ? "default" : "outline"}
                        onClick={() => togglePlayerInLineup(p.id, "remplacant")}
                        className={lp?.role === "remplacant" ? "bg-[#FBC02D] text-[#121212] hover:bg-[#F9A825]" : ""}
                        data-testid={`ms-remplacant-${p.id}`}
                      >
                        Remplaçant
                      </Button>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Score & événements (post-match) */}
            <section>
              <h3 className="text-xs tracking-overline text-[#616161] mb-2">Résultat et événements</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <Label>{form.home_away === "domicile" ? `Score ${user?.club?.name || "Nous"}` : "Score adverse"}</Label>
                  <Input type="number" min="0" value={form.score_home} onChange={(e) => setForm({ ...form, score_home: e.target.value })} data-testid="ms-score-home" />
                </div>
                <div>
                  <Label>{form.home_away === "domicile" ? "Score adverse" : `Score ${user?.club?.name || "Nous"}`}</Label>
                  <Input type="number" min="0" value={form.score_away} onChange={(e) => setForm({ ...form, score_away: e.target.value })} data-testid="ms-score-away" />
                </div>
              </div>

              <div className="space-y-2">
                {form.events.map((ev, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 bg-[#F4F5F7] rounded border border-[#E0E0E0]">
                    <div className="col-span-1">
                      <Label className="text-[10px]">Min.</Label>
                      <Input type="number" min="0" max="120" value={ev.minute} onChange={(e) => updateEvent(idx, { minute: e.target.value })} className="h-8" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Type</Label>
                      <Select value={ev.type} onValueChange={(v) => updateEvent(idx, { type: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {EVENT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Joueur{ev.type === "remplacement" ? " (sortant)" : ""}</Label>
                      <Select value={ev.player_id || "none"} onValueChange={(v) => updateEvent(idx, { player_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {ev.type === "remplacement" && (
                      <div className="col-span-3">
                        <Label className="text-[10px]">Entrant</Label>
                        <Select value={ev.player_in_id || "none"} onValueChange={(v) => updateEvent(idx, { player_in_id: v === "none" ? "" : v })}>
                          <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.last_name} {p.first_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className={ev.type === "remplacement" ? "col-span-2" : "col-span-5"}>
                      <Label className="text-[10px]">Note</Label>
                      <Input value={ev.note} onChange={(e) => updateEvent(idx, { note: e.target.value })} className="h-8" />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeEvent(idx)}>
                        <X className="w-3.5 h-3.5 text-[#C62828]" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addEvent} data-testid="ms-add-event">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un événement
                </Button>
              </div>
            </section>

            <section>
              <Label>Notes / Observations</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Tactique, faits marquants, blessures..." />
            </section>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="ms-submit-btn">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
